/* Velours product page behaviour.
   Loaded on the product template only. Handles the single-frame gallery,
   the native variant picker, the styling-moment card, and the save toggle.

   Adding to cart is not handled here: the form carries `data-cart-form`, so
   melanina.js performs the AJAX add and opens the cart drawer, exactly like
   every other add-to-bag in the theme. Without JavaScript the form still
   posts to /cart/add and the first media is shown, so the page stays usable. */
(function () {
  "use strict";

  function $(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function $$(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function pad(value) {
    return String(value).length < 2 ? "0" + value : String(value);
  }

  function reduceMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function inViewport(element) {
    var box = element.getBoundingClientRect();
    return box.top < window.innerHeight * 0.75 && box.bottom > window.innerHeight * 0.25;
  }

  /* ------------------------------------------------- gallery: stacked layout
     Every photograph is already in the flow, so nothing is shown or hidden.
     The rail becomes an index: it follows the scroll position, and clicking a
     number scrolls to that photograph. */
  function initStackGallery(root, media, rail, counter) {
    var index = -1;

    function mark(next) {
      if (next === index) return;
      index = next;
      rail.forEach(function (item, i) {
        var active = i === index;
        item.classList.toggle("active", active);
        item.setAttribute("aria-pressed", active ? "true" : "false");
      });
      if (counter) counter.textContent = pad(index + 1) + " / " + pad(media.length);
    }

    function scrollToIndex(i) {
      if (!media[i]) return;
      media[i].scrollIntoView({ behavior: reduceMotion() ? "auto" : "smooth", block: "start" });
    }

    rail.forEach(function (item, i) {
      item.addEventListener("click", function () {
        scrollToIndex(i);
      });
    });

    if ("IntersectionObserver" in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            var position = media.indexOf(entry.target);
            if (position > -1) mark(position);
          });
        },
        { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
      );
      media.forEach(function (item) {
        observer.observe(item);
      });
    }

    mark(0);

    return {
      showMediaId: function (mediaId) {
        if (!mediaId) return;
        for (var i = 0; i < media.length; i += 1) {
          if (media[i].getAttribute("data-media-id") !== String(mediaId)) continue;
          /* Only move the page when the variant's photograph is off screen, so
             choosing a colour never yanks the layout out from under a tap. */
          if (!inViewport(media[i])) scrollToIndex(i);
          return;
        }
      }
    };
  }

  /* ---------------------------------------------- gallery: single-frame layout */
  function initGallery(root) {
    var frame = $("[data-gallery-frame]", root);
    if (!frame) return null;

    var modeHost = $("[data-gallery-mode]", root);
    var mode = modeHost ? modeHost.getAttribute("data-gallery-mode") : "frame";
    var media = $$("[data-gallery-media]", frame);
    var thumbs = $$("[data-gallery-thumb]", root);
    var rail = $$("[data-gallery-rail-item]", root);
    var counter = $("[data-gallery-counter]", root);
    if (media.length < 1) return null;

    if (mode === "stack") return initStackGallery(root, media, rail, counter);

    var index = 0;

    function show(next) {
      index = (next + media.length) % media.length;

      media.forEach(function (item, i) {
        var active = i === index;
        item.hidden = !active;
        var video = item.querySelector("video");
        if (video && !active && typeof video.pause === "function") video.pause();
      });

      thumbs.forEach(function (thumb, i) {
        var active = i === index;
        thumb.classList.toggle("active", active);
        thumb.setAttribute("aria-pressed", active ? "true" : "false");
      });

      rail.forEach(function (item, i) {
        var active = i === index;
        item.classList.toggle("active", active);
        item.setAttribute("aria-pressed", active ? "true" : "false");
      });

      if (counter) counter.textContent = pad(index + 1) + " / " + pad(media.length);
    }

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener("click", function () {
        show(i);
      });
    });

    rail.forEach(function (item, i) {
      item.addEventListener("click", function () {
        show(i);
      });
    });

    show(0);

    return {
      showMediaId: function (mediaId) {
        if (!mediaId) return;
        for (var i = 0; i < media.length; i += 1) {
          if (media[i].getAttribute("data-media-id") === String(mediaId)) {
            show(i);
            return;
          }
        }
      }
    };
  }

  /* --------------------------------------------------------- variant picker */
  function initVariants(root, gallery) {
    var dataNode = $("[data-variant-data]", root);
    var form = $("[data-product-form]", root);
    if (!dataNode || !form) return;

    var variants = [];
    try {
      variants = JSON.parse(dataNode.textContent) || [];
    } catch (error) {
      variants = [];
    }
    if (!variants.length) return;

    var idField = form.querySelector('[name="id"]');
    var submit = form.querySelector('[type="submit"]');
    var submitLabel = submit ? submit.querySelector("[data-add-text]") : null;
    var priceHost = $("[data-variant-price]", root);
    var compareHost = $("[data-variant-compare]", root);
    var saveHost = $("[data-variant-save]", root);
    var availabilityHost = $("[data-variant-availability]", root);
    var messageHost = $("[data-selection-message]", root);
    var optionInputs = $$("input[data-option-index]", root);

    function chosenOptions() {
      var values = [];
      optionInputs.forEach(function (input) {
        if (input.checked) values[Number(input.getAttribute("data-option-index"))] = input.value;
      });
      return values;
    }

    function matchVariant() {
      var chosen = chosenOptions();
      if (!chosen.length) return variants[0];

      for (var i = 0; i < variants.length; i += 1) {
        var variant = variants[i];
        var matches = true;
        for (var o = 0; o < chosen.length; o += 1) {
          if (chosen[o] !== undefined && variant.options[o] !== chosen[o]) {
            matches = false;
            break;
          }
        }
        if (matches) return variant;
      }
      return null;
    }

    /* Dim option values that cannot be combined with the current selection. */
    function markUnavailable() {
      var chosen = chosenOptions();
      $$("[data-option-choice]", root).forEach(function (choice) {
        var input = choice.querySelector("input[data-option-index]");
        if (!input) return;
        var position = Number(input.getAttribute("data-option-index"));
        var candidate = chosen.slice();
        candidate[position] = input.value;

        var reachable = variants.some(function (variant) {
          if (!variant.available) return false;
          for (var i = 0; i < candidate.length; i += 1) {
            if (i === position) continue;
            if (candidate[i] !== undefined && variant.options[i] !== candidate[i]) return false;
          }
          return variant.options[position] === input.value;
        });

        choice.classList.toggle("is-unavailable", !reachable);
        /* Mirrors :has(input:checked) for browsers without :has() support. */
        choice.classList.toggle("is-selected", input.checked);
      });
    }

    function renderSelectionMessage() {
      if (!messageHost) return;
      var template = messageHost.getAttribute("data-selection-template") || "%s";
      var note = messageHost.getAttribute("data-selection-note") || "";
      var values = chosenOptions().filter(function (value) {
        return value !== undefined && value !== "";
      });
      if (!values.length) {
        messageHost.hidden = !note;
        messageHost.querySelector("span").textContent = note;
        return;
      }
      messageHost.hidden = false;
      messageHost.querySelector("span").textContent = (template.replace("%s", values.join(" / ")) + " " + note).trim();
    }

    function setUnavailable() {
      if (!submit) return;
      submit.disabled = true;
      if (submitLabel) submitLabel.textContent = submit.getAttribute("data-unavailable-label") || "Indisponível";
    }

    function render() {
      markUnavailable();
      renderSelectionMessage();

      var variant = matchVariant();
      if (!variant) {
        setUnavailable();
        return;
      }

      if (idField) idField.value = variant.id;
      if (priceHost) priceHost.innerHTML = variant.price;

      if (compareHost) {
        compareHost.innerHTML = variant.compare_at_price || "";
        compareHost.hidden = !variant.compare_at_price;
      }
      if (saveHost) saveHost.hidden = !variant.compare_at_price;

      if (availabilityHost) {
        availabilityHost.classList.toggle("is-out", !variant.available);
        var availabilityText = availabilityHost.querySelector("span");
        if (availabilityText) availabilityText.textContent = variant.inventory_label || "";
      }

      if (submit) {
        submit.disabled = !variant.available;
        if (submitLabel) {
          submitLabel.textContent = variant.available
            ? submit.getAttribute("data-add-label") || "Adicionar ao saco"
            : submit.getAttribute("data-sold-out-label") || "Esgotado";
        }
      }

      if (gallery && variant.featured_media_id) gallery.showMediaId(variant.featured_media_id);

      if (window.history.replaceState && variant.id) {
        var url = new URL(window.location.href);
        url.searchParams.set("variant", variant.id);
        window.history.replaceState({}, "", url.toString());
      }
    }

    optionInputs.forEach(function (input) {
      input.addEventListener("change", render);
    });

    render();
  }

  /* The quantity stepper is not handled here: melanina.js already delegates
     [data-quantity-step] clicks document-wide, and the markup uses the same
     [data-quantity] wrapper. Adding a second listener would double-step it. */

  /* ------------------------------------------------- styling-moment card */
  function initEntrances(root) {
    var card = $("[data-entrance-card]", root);
    if (!card) return;

    var options = $$("[data-entrance-option]", card);
    var titleHost = $("[data-entrance-title]", card);
    var noteHost = $("[data-entrance-note]", card);
    var counter = $("[data-entrance-counter]", card);
    if (options.length < 1) return;

    function select(index) {
      options.forEach(function (option, i) {
        var active = i === index;
        option.classList.toggle("active", active);
        option.setAttribute("aria-pressed", active ? "true" : "false");
      });
      var chosen = options[index];
      if (titleHost) titleHost.textContent = chosen.getAttribute("data-entrance-title") || "";
      if (noteHost) noteHost.textContent = chosen.getAttribute("data-entrance-note") || "";
      if (counter) counter.textContent = pad(index + 1);
    }

    options.forEach(function (option, i) {
      option.addEventListener("click", function () {
        select(i);
      });
    });

    select(0);
  }

  /* --------------------------------------------------------- save for later */
  function initSave(root) {
    var heart = $("[data-save-note]", root);
    if (!heart) return;
    heart.addEventListener("click", function () {
      var active = heart.classList.toggle("is-active");
      heart.setAttribute("aria-pressed", active ? "true" : "false");
      var label = active ? heart.getAttribute("data-label-saved") : heart.getAttribute("data-label-save");
      if (label) heart.setAttribute("aria-label", label);
    });
  }

  /* ------------------------------------------------------- motion video card */
  function initMotionCard(root) {
    var video = $("[data-motion-video]", root) || $(".gallery-motion-card video", root);
    if (!video) return;
    if (reduceMotion()) {
      video.removeAttribute("autoplay");
      video.setAttribute("controls", "controls");
      if (typeof video.pause === "function") video.pause();
      return;
    }
    var attempt = video.play();
    if (attempt && typeof attempt.catch === "function") {
      attempt.catch(function () {
        /* Autoplay refused: the poster frame stays visible, which is fine. */
      });
    }
  }

  /* -------------------------------------------------------- add-to-bag celebration
     After melanina.js completes the AJAX add (via data-cart-form), it fires
     `melanina:cart:updated`. We listen for that to trigger the flight animation,
     the button confirmation state, the bag ping, and the corner-bag reveal.

     The form submit itself is still handled by melanina.js — we only add the
     visual feedback layer on top. */
  function initAddCelebration(root) {
    var form = $("[data-cart-form]", root);
    var button = $(".purchase-button", root);
    var buttonIcon = button ? button.querySelector(".purchase-button-icon") : null;
    var bagAction = $("[data-cart-open]") || $(".bag-action");
    var cornerBag = $("[data-corner-bag]");
    var flightHolder = $("[data-cart-flight]", root) || $("[data-cart-flight]");
    if (!form || !button) return;

    var celebrating = false;

    function celebrate() {
      if (celebrating) return;
      celebrating = true;

      /* Button confirmation state */
      button.classList.add("is-added");
      window.setTimeout(function () {
        button.classList.remove("is-added");
        celebrating = false;
      }, 2200);

      /* Bag action ping */
      if (bagAction) {
        bagAction.classList.add("is-celebrating");
        var ping = document.createElement("i");
        ping.className = "bag-ping";
        ping.setAttribute("aria-hidden", "true");
        bagAction.appendChild(ping);
        window.setTimeout(function () {
          bagAction.classList.remove("is-celebrating");
          if (ping.parentNode) ping.parentNode.removeChild(ping);
        }, 1400);
      }

      /* Cart flight animation */
      if (flightHolder && !reduceMotion()) {
        flightHolder.hidden = false;
        window.setTimeout(function () {
          flightHolder.hidden = true;
        }, 1100);
      }

      /* Corner bag reveal */
      if (cornerBag) {
        cornerBag.hidden = false;
        cornerBag.classList.add("is-new");
        window.setTimeout(function () {
          cornerBag.classList.remove("is-new");
        }, 1300);
      }
    }

    /* Listen for the custom event melanina.js dispatches after a successful add. */
    document.addEventListener("melanina:cart:updated", function () {
      /* Only celebrate if this product page initiated the add (button was busy). */
      if (button.getAttribute("aria-busy") === "true" || button.classList.contains("is-added")) return;
      /* The event fires for drawer quantity changes too — only celebrate when
         the button was the last interaction. We use a short flag set on submit. */
      if (form.__veloursSubmitting) {
        form.__veloursSubmitting = false;
        celebrate();
      }
    });

    /* Mark that this form initiated the add, so we know the cart:updated event
       that follows should trigger celebration. */
    form.addEventListener("submit", function () {
      form.__veloursSubmitting = true;
    });
  }

  /* -------------------------------------------------------- buy now to branded bag
     Standard Shopify stores must complete payment in Shopify Checkout. This keeps
     the customer inside Melanina's product + cart experience until that secure
     handoff: it submits the selected native variant, then opens the cart drawer. */
  function initBuyNow(root) {
    var form = $("[data-cart-form]", root);
    var button = $("[data-buy-now]", root);
    if (!form || !button || button.__veloursBuyNow) return;
    button.__veloursBuyNow = true;

    var defaultLabel = button.getAttribute("data-buy-now-label") || button.textContent;
    var busyLabel = button.getAttribute("data-buy-now-busy-label") || "A adicionar…";
    var doneLabel = button.getAttribute("data-buy-now-done-label") || "No saco — ver saco";
    var label = button.querySelector("span");

    button.addEventListener("click", function () {
      var submit = $(".purchase-button", form);
      if (!submit || submit.disabled) return;
      form.__veloursBuyNow = true;
      button.disabled = true;
      button.setAttribute("aria-busy", "true");
      if (label) label.textContent = busyLabel;
      if (form.requestSubmit) form.requestSubmit(submit);
      else submit.click();
    });

    document.addEventListener("melanina:cart:updated", function () {
      if (!form.__veloursBuyNow) return;
      form.__veloursBuyNow = false;
      button.disabled = false;
      button.removeAttribute("aria-busy");
      if (label) label.textContent = doneLabel;
      var cartOpen = $("[data-cart-open]");
      if (cartOpen) cartOpen.click();
      window.setTimeout(function () {
        if (label) label.textContent = defaultLabel;
      }, 2600);
    });
  }

  /* ------------------------------------------------ recommendation carousel */
  function initRecommendations(root) {
    var track = $(".recommended-grid", root);
    if (!track || track.__veloursRecommendations) return;
    var cards = $$(".recommended-card", track);
    if (cards.length < 2) return;
    track.__veloursRecommendations = true;
    track.classList.add("is-auto-scroll");
    track.setAttribute("role", "region");
    track.setAttribute("aria-label", "Peças recomendadas com deslocamento automático");

    if (reduceMotion()) return;
    var timer = null;
    var paused = false;
    function next() {
      if (paused || document.hidden) return;
      var card = cards[0];
      var gap = parseFloat(window.getComputedStyle(track).gap || "0") || 0;
      var step = card.getBoundingClientRect().width + gap;
      var limit = track.scrollWidth - track.clientWidth;
      if (track.scrollLeft >= limit - 4) track.scrollTo({ left: 0, behavior: "smooth" });
      else track.scrollBy({ left: step, behavior: "smooth" });
    }
    function start() {
      if (timer) return;
      timer = window.setInterval(next, 4200);
    }
    function stop() {
      if (!timer) return;
      window.clearInterval(timer);
      timer = null;
    }
    track.addEventListener("mouseenter", function () { paused = true; stop(); });
    track.addEventListener("mouseleave", function () { paused = false; start(); });
    track.addEventListener("focusin", function () { paused = true; stop(); });
    track.addEventListener("focusout", function (event) { if (!track.contains(event.relatedTarget)) { paused = false; start(); } });
    track.addEventListener("touchstart", function () { paused = true; stop(); }, { passive: true });
    track.addEventListener("touchend", function () { window.setTimeout(function () { paused = false; start(); }, 1200); }, { passive: true });
    document.addEventListener("visibilitychange", function () { if (document.hidden) stop(); else if (!paused) start(); });
    start();
  }

  /* -------------------------------------------------------- corner bag count sync */
  function initCornerBag() {
    var cornerBag = $("[data-corner-bag]");
    if (!cornerBag) return;

    var countNode = cornerBag.querySelector("[data-corner-count]");

    function sync() {
      /* Read the count from the header badge (melanina.js keeps it current). */
      var source = $("[data-cart-count]");
      var count = source ? parseInt(source.textContent, 10) || 0 : 0;
      if (countNode) countNode.textContent = count;
      /* Show once an item exists, hide when the cart empties. */
      cornerBag.hidden = count < 1;
    }

    document.addEventListener("melanina:cart:updated", sync);
    sync();
  }

  function bootstrap(scope) {
    $$("[data-velours-product]", scope).forEach(function (root) {
      var gallery = initGallery(root);
      initVariants(root, gallery);
      initEntrances(root);
      initSave(root);
      initMotionCard(root);
      initAddCelebration(root);
      initBuyNow(root);
      initRecommendations(root);
    });
    initCornerBag();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      bootstrap(document);
    });
  } else {
    bootstrap(document);
  }

  /* Theme editor re-renders sections in place. */
  document.addEventListener("shopify:section:load", function (event) {
    bootstrap(event.target);
  });
})();
